// Wizard chọn AI provider cho workspace — hybrid logic:
//   1) Nếu detect được ~/.claude/settings.json global có ANTHROPIC_BASE_URL + token
//      → hỏi user "Dùng global hay setup riêng?"
//   2) Nếu user setup riêng (hoặc không có global) → hỏi "Subscription / LLMLite?"
// Output: discriminated union — caller branch theo provider tiếp tục phase 03 (sub) hoặc 05 (llmlite).
import { readFileSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";
import { select } from "@inquirer/prompts";

// AI provider options:
//   - subscription : Claude Code Pro/Max OAuth (no API key — Anthropic billing qua subscription)
//   - llmlite      : NAL gateway ai.nal.vn (key sk-pvd... bypass billing direct)
//   - anthropic    : Anthropic API direct (key sk-ant-... pay-as-you-go credit) (v1.6.0)
//   - use-global   : Copy ~/.claude/settings.json (đã có config sẵn từ Claude Code global)
//
// v1.20.3: Codex/Gemini providers removed — Claude Code native không support
// OpenAI/Gemini. Để dùng OPENAI_API_KEY / GEMINI_API_KEY cho skills khác
// (ai-multimodal, vv) → 'avatar secrets set OPENAI_API_KEY' / 'GEMINI_API_KEY'.
export type AiProviderChoice = "use-global" | "subscription" | "llmlite" | "anthropic";

// Snapshot info global config — KHÔNG return raw token (security).
// Token presence chỉ check, value không expose ra UI / log / return.
export interface GlobalClaudeSettingsInfo {
  exists: boolean;
  hasBaseUrl: boolean;
  baseUrl?: string; // OK expose vì base URL là endpoint public (vd https://ai.nal.vn)
  hasToken: boolean;
  model?: string;
  rawSettings?: Record<string, unknown>; // Để caller (use-global path) copy
}

// Path mặc định Claude Code đọc settings — không config khác.
function getGlobalSettingsPath(): string {
  return join(homedir(), ".claude", "settings.json");
}

// Read + validate ~/.claude/settings.json an toàn. Không throw — graceful null-ish.
export function detectGlobalClaudeSettings(): GlobalClaudeSettingsInfo {
  const path = getGlobalSettingsPath();
  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch {
    return { exists: false, hasBaseUrl: false, hasToken: false };
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    // JSON corrupted — treat as no global config (an toàn hơn dùng config rác).
    return { exists: true, hasBaseUrl: false, hasToken: false };
  }

  const env = (parsed.env as Record<string, unknown> | undefined) || {};
  const baseUrl = typeof env.ANTHROPIC_BASE_URL === "string" ? env.ANTHROPIC_BASE_URL : undefined;
  const hasToken =
    typeof env.ANTHROPIC_AUTH_TOKEN === "string" && env.ANTHROPIC_AUTH_TOKEN.length > 0;
  const model = typeof parsed.model === "string" ? parsed.model : undefined;

  return {
    exists: true,
    hasBaseUrl: !!baseUrl,
    baseUrl,
    hasToken,
    model,
    rawSettings: parsed,
  };
}

// Main wizard. Trả enum choice — caller branch theo phase 03 / 05 / use-global path.
// `globalInfo` injectable cho test (default = detect thật).
export async function promptAiProviderChoice(
  globalInfo: GlobalClaudeSettingsInfo = detectGlobalClaudeSettings(),
): Promise<AiProviderChoice> {
  // Hybrid path: chỉ offer use-global khi global có ĐỦ baseUrl + token (config full).
  if (globalInfo.exists && globalInfo.hasBaseUrl && globalInfo.hasToken) {
    const choice = (await select({
      message: `Phát hiện AI config global (base URL: ${globalInfo.baseUrl}). Dùng cho project này?`,
      choices: [
        {
          name: "a. Yes — copy config global vào .claude/settings.json (per-project)",
          value: "use-global" as const,
        },
        {
          name: "b. No — setup riêng (chọn provider khác)",
          value: "setup-fresh" as const,
        },
      ],
    })) as "use-global" | "setup-fresh";

    if (choice === "use-global") return "use-global";
    // Fall-through to provider picker.
  }

  // Provider picker — 3 paths (Subscription / LLMLite / Anthropic Direct).
  return (await select({
    message: "Chọn provider cho AI tools:",
    choices: [
      {
        name: "1. Claude Code Subscription (dùng quota cá nhân Anthropic, OAuth login)",
        value: "subscription" as const,
      },
      {
        name: "2. LLM key NAL (ai.nal.vn — gateway nội bộ, key sk-...)",
        value: "llmlite" as const,
      },
      {
        name: "3. Anthropic API key trực tiếp (console.anthropic.com, key sk-ant-...)",
        value: "anthropic" as const,
      },
    ],
  })) as "subscription" | "llmlite" | "anthropic";
}
